import {useFormContext} from 'react-hook-form';
import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import {useDebouncedCallback} from 'use-debounce';

// eslint-disable-next-line import/prefer-default-export
export function useFormFieldWarning<
    TFieldValues extends Record<string, any>,
    TFieldName extends string,
>({
    name, validate: validateValue, onChangeDebounceDelay = 500,
}: {
    name: TFieldName,
    validate(
        value: TFieldValues[TFieldName]
    ): string | false | Promise<string | false>,
    onChangeDebounceDelay?: number,
}): {
    warning: string | false,
    triggerWarning(): void,
} {
    const [warning, setWarning] = useState<string | false>(false);

    const validateValueRef = useRef(validateValue);

    validateValueRef.current = validateValue;

    const {watch, getValues} = useFormContext<TFieldValues>();

    const validate = useCallback((): void => {
        const value = getValues()[name];

        Promise.resolve(validateValueRef.current(value))
            .then(result => {
                setWarning(result);
            });
    }, [getValues, name]);

    const {
        flush: validateDebounced,
        cancel: cancelValidateDebounce,
    } = useDebouncedCallback(validate, onChangeDebounceDelay);

    const value = watch()[name];

    useEffect((): void => {
        setWarning(false);
        cancelValidateDebounce();
        validateDebounced();
    }, [
        value,
        cancelValidateDebounce,
        validateDebounced,
    ]);

    return {
        warning,
        triggerWarning: validate,
    };
}
